/**
 * ThemedCard - Native div with automatic dark mode theming
 *
 * Pure native wrapper component to provide consistent theming
 * without requiring manual dark mode classes on every usage.
 *
 * Benefits:
 * - DRY: Background colors defined once, not repeated 20+ times
 * - Maintainable: Change theme in one place
 * - Clean API: Components just use <ThemedCard> without dark mode boilerplate
 * - Variants: Can add interactive, elevated, etc. without duplicating classes
 */

import type React from 'react';

interface ThemedCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'interactive';
}

export const ThemedCard = ({
  children,
  className = '',
  variant = 'default',
  ...props
}: ThemedCardProps) => {
  // Base theming - applied to all cards
  const baseClasses =
    'bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700';

  // Variant-specific styling
  const variantClasses =
    variant === 'interactive'
      ? 'hover:shadow-lg hover:border-sky-500 dark:hover:border-sky-400 transition-all cursor-pointer'
      : '';

  return (
    <div className={`${baseClasses} ${variantClasses} ${className}`} {...props}>
      {children}
    </div>
  );
};

export default ThemedCard;
